import { FieldValues, UseFormReturn } from 'react-hook-form';
import {
    FormContainer,
    SelectElement,
    TextFieldElement,
} from 'react-hook-form-mui';
import * as Yup from 'yup';
import { Stack, Typography } from '@mui/material';

import { EditButtons } from '@barboxx/admin-components';
import { BusinessSignup } from '@barboxx/models';
import { useBool } from '@barboxx/react-utils';
import { BUSINESS_SIGNUP_STATUS, BusinessSignupStatus } from '@barboxx/types';
import {
    enumToFormOptions,
    enumToList,
    StateList,
    stateListToFormOptions,
} from '@barboxx/utils';

export const BUSINESS_SIGNUP_SCHEMA: Yup.ObjectSchema<BusinessSignup.UpdateRequest> =
    Yup.object({
        id: Yup.number().required(),
        firstName: Yup.string().required('Required'),
        lastName: Yup.string().required('Required'),
        phone: Yup.string().length(10).required('Required'),
        email: Yup.string()
            .email('Must match and email address')
            .required('Required'),
        businessName: Yup.string().required('Required'),
        address: Yup.object({
            street: Yup.string().required('Required'),
            city: Yup.string().required('Required'),
            state: Yup.string().required('Required'),
            zip: Yup.string().required('Required'),
        }).required('Required'),
        comment: Yup.string(),
        status: Yup.string()
            .oneOf<BusinessSignupStatus>(enumToList(BUSINESS_SIGNUP_STATUS))
            .required(),
    });

export const CREATE_BUSINESS_SIGNUP_SCHEMA: Yup.ObjectSchema<BusinessSignup.CreateRequest> =
    BUSINESS_SIGNUP_SCHEMA.omit(['id']);

interface BusinessDetailsFormProps<T extends FieldValues> {
    formContext: UseFormReturn<T>;
    onSubmit: (values: T) => void;
}

export function BusinessDetailsForm<T extends FieldValues = BusinessSignup>({
    formContext,
    onSubmit,
}: BusinessDetailsFormProps<T>) {
    const [editable, editableActions] = useBool();

    const handleCancel = () => {
        editableActions.off();

        formContext.reset();
    };

    return (
        <FormContainer onSuccess={onSubmit} formContext={formContext}>
            <Stack gap={2}>
                <TextFieldElement
                    label="Business Name"
                    name="businessName"
                    required
                    autoFocus
                    autoComplete="organization"
                    disabled={!editable}
                />
                <Typography variant="subtitle1" color="textPrimary">
                    Contact Info
                </Typography>
                <TextFieldElement
                    label="First Name"
                    name="firstName"
                    fullWidth
                    required
                    autoComplete="given-name"
                    disabled={!editable}
                />
                <TextFieldElement
                    label="Last Name"
                    name="lastName"
                    fullWidth
                    required
                    autoComplete="family-name"
                    disabled={!editable}
                />
                <TextFieldElement
                    label="Phone"
                    name="phone"
                    fullWidth
                    required
                    autoComplete="tel"
                    disabled={!editable}
                />
                <TextFieldElement
                    label="Email"
                    name="email"
                    fullWidth
                    required
                    autoComplete="email"
                    disabled={!editable}
                />
                <Typography variant="subtitle1" color="textPrimary">
                    Address
                </Typography>
                <TextFieldElement
                    required
                    label="Street"
                    name="address.street"
                    fullWidth
                    autoComplete="street-address"
                    disabled={!editable}
                />
                <TextFieldElement
                    required
                    label="City"
                    name="address.city"
                    fullWidth
                    autoComplete="address-level2"
                    disabled={!editable}
                />
                <Stack direction="row" gap={1}>
                    <SelectElement
                        required
                        sx={{ flex: 1 }}
                        label="State"
                        name="address.state"
                        autoComplete="address-level1"
                        disabled={!editable}
                        options={stateListToFormOptions(StateList)}
                        SelectProps={{
                            native: true,
                        }}
                    />
                    <TextFieldElement
                        required
                        sx={{ flex: 1 }}
                        label="Zip Code"
                        name="address.zip"
                        autoComplete="postal-code"
                        disabled={!editable}
                    />
                </Stack>
                <Typography variant="subtitle1" color="textPrimary">
                    Questions?
                </Typography>
                <TextFieldElement
                    label="Comments"
                    name="comment"
                    fullWidth
                    disabled
                    multiline
                    rows={3}
                />
                <Typography variant="subtitle1" color="textPrimary">
                    Status
                </Typography>
                <SelectElement
                    required
                    sx={{ flex: 1 }}
                    label="Status"
                    name="status"
                    options={enumToFormOptions(BUSINESS_SIGNUP_STATUS)}
                    slotProps={{
                        input: {
                            readOnly: !editable,
                        },
                    }}
                />
                <EditButtons
                    formContext={formContext}
                    handleCancel={handleCancel}
                    editable={editable}
                    editableActions={editableActions}
                />
            </Stack>
        </FormContainer>
    );
}
